import { FC, ReactNode, useEffect, useRef, useState } from "react"
import React from "react"
import { SxProps } from "@mui/system"

import {
  Autocomplete,
  Box,
  styled,
  TextField,
} from "../../mui.components"

interface items {
  label: string
  key: string
}

export interface IAutoCompleteProps {
  /**
   * Unique identifier for select
   */
  id: string

  /**
   *  label for select
   */
  label: string
  value: items | null

  /**
   * value holding the selected value
   */
  defaultValue?: string
  /**
   * To get selected value
   * @param value
   * @returns
   */
  onChangeHandler?: (value: string) => void

  /**
   * We can pass the optiona as two ways
   * option 1:
   *  - value as array of [{key: string, value: string}]
   * option 2:
   *  - value as react element
   */
  options: ReactNode | items[]
  isHeader?: boolean
  hasError: boolean
  sxProp: SxProps
  customPopupIcon?: ReactNode
  onBlur?: () => void
  isInputLabel?: boolean
  callback?: (e) => void
  isNotPlaceholder?: boolean
  disabled?: boolean
}

const UAutoComplete: FC<IAutoCompleteProps> = ({
  id,
  label,
  options,
  hasError,
  onChangeHandler,
  sxProp,
  value,
  defaultValue,
  isHeader,
  customPopupIcon,
  onBlur,
  isInputLabel,
  callback,
  isNotPlaceholder,
  disabled=false,
}) => {
  const [inputValue, setInputValue] = useState("")
  const suggestionRef = useRef({
    input: "",
    isSuggested: false,
  })

  const GroupHeader = styled("div")(({ theme }) => ({
    position: "sticky",
    padding: "4px 10px",
    color: "#000000",
    fontSize: "12px",
    fontWeight: "400",
    opacity: "0.6",
  }))

  const GroupItems = styled("ul")({
    padding: 0,
    "& .MuiAutocomplete-option": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  })

  const Lists = options.map((option) => {
    const category = option.category
    return {
      category: category,
      ...option,
    }
  })

  useEffect(() => {
    setInputValue(value?.label ?? "")
  }, [value])

  useEffect(() => {
    suggestionRef.current.input = defaultValue || ""
    setInputValue(defaultValue)
  }, [defaultValue])

  const handleIsOptionEqualToValue = (option, val) => {
    const isExactMatch = option.label === inputValue

    if (isExactMatch) {
      suggestionRef.current.isSuggested = false // Reset for new exact match
      suggestionRef.current.input = ""
      return true // Always select exact matches
    }

    if (!inputValue) {
      suggestionRef.current.isSuggested = false
      suggestionRef.current.input = ""
      return false
    }

    const isMatch = (option.label || "")
      .toLowerCase()
      .includes(inputValue.toLowerCase())

    if (isMatch) {
      if (suggestionRef.current.input !== inputValue) {
        // New input value, reset suggestion
        suggestionRef.current.isSuggested = false
        suggestionRef.current.input = inputValue
      }

      if (!suggestionRef.current.isSuggested) {
        suggestionRef.current.isSuggested = true

        return true // Select the first partial match
      }
    }

    return false // Do not select other partial matches
  }

  return (
    <>
      <Autocomplete
        onBlur={onBlur}
        sx={{
          ...sxProp,
          ".MuiAutocomplete-endAdornment": {
            right: isHeader ? "-12px !important" : "initial",
          },
          ".MuiAutocomplete-inputFocused": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: isHeader ? "195px" : "inherit",
          },
          ".MuiAutocomplete-inputRoot": {
            maxWidth: isHeader ? "195px" : "inherit",
          },
          "& input::placeholder": {
            color: hasError ? "error.main" : "",
            opacity: hasError ? "100%" : "",
          },
        }}
        popupIcon={customPopupIcon && customPopupIcon}
        onChange={(event: any, newValue: items) => {
          onChangeHandler && onChangeHandler(newValue?.id || null)
          callback && callback(newValue)
        }}
        value={inputValue || null}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue)
        }}
        id={id}
        options={Lists}
        isOptionEqualToValue={handleIsOptionEqualToValue}
        groupBy={(list) => list.category}
        renderInput={(params: any) => (
          <TextField
            {...params}
            label={isInputLabel ? label : ""}
            name={id}
            placeholder={isNotPlaceholder ? "" : label}
            error={hasError}
          />
        )}
        renderGroup={(params) => (
          <Box
            component={"li"}
            key={params.key}
            sx={{
              position: "relative",
              borderBottom: "1px solid #e0e0e0",
              "&:last-child": {
                borderBottom: "none",
              },
            }}
          >
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </Box>
        )}
        disabled={disabled}
      />
    </>
  )
}

export default UAutoComplete
